import { GlobalStyle } from 'core/style/global'
import { TypeTheme, TypeConfig } from 'core/style/theme'
import React, { Suspense, useCallback, useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { getUA, setConfig, setPartnerId } from 'core/singleton'
import { BrowserRouter, HashRouter, Route, StaticRouter, Switch } from 'react-router-dom'
import PageLayout from 'container/PageLayout'
import useMediaQuery from 'core/hooks/useMediaQuery'
import { lazyWithPreload } from 'core/util/preload'
import { BRIDGE } from 'core/constants/bridge'
import { IS_OPEN_LINK, STORE_MODULE, STORE_ROOT_PROPS } from 'core/store/global'
import { GAHelper } from 'core/ga'
import PartnersRouter from 'routes/PartnersRouter'
import useAnalysis from 'core/hooks/useAnalysis'
import { PageSearch, PageSearchResult } from 'container/Page'
import { MODULE_NAME } from 'core/constants'
import { requestBridge } from 'core/util/default'
import useSetThemeEventListener from 'core/hooks/useSetThemeEventListener'

const PageCuration = lazyWithPreload(() => import('container/Page/PageCuration'))
const PageMain = lazyWithPreload(() => import('container/Page/PageMain'))
const PageSchedule = lazyWithPreload(() => import('container/Page/PageSchedules'))

export interface ModuleProps {
  partnerId: string
  accessToken: string
  broadcast: boolean
  broadcastDisplayType?: 'horizontal' | 'vertical'
  broadcastOpenLink: string
  broadcastTable: boolean
  broadcastTitle?: string
  schedule: boolean
  scheduleType: 'image' | 'feed' | 'list'
  curation: boolean
  curationDetail: boolean
  autoPlay: boolean
  searchBar: boolean
  sampleTitle: boolean
  bridge: boolean
  router: boolean
  theme: TypeTheme
  themeB: TypeTheme
  config: TypeConfig
  contentBanner: boolean
  textBanner: boolean
  curationId?: string
  topBanner?: boolean
  domListLength?: number
  token?: TokenResponse
  memberType?: string
  sauceClip?: boolean
  memberId?: string
  renderModuleName?: MODULE_NAME
}

const Module: React.FC<ModuleProps> = (props) => {
  const { theme, themeB, config, partnerId, router } = props
  const { hashRouter } = config

  const [rootProps, setRootProps] = useRecoilState(STORE_ROOT_PROPS)
  const setIsOpenLink = useSetRecoilState(IS_OPEN_LINK)
  const setModuleName = useSetRecoilState(STORE_MODULE)

  const isDeskTop = useMediaQuery('DESKTOP')

  const getIsOpenLink = useCallback(() => {
    if (!rootProps?.broadcastOpenLink) return false
    const list = rootProps?.broadcastOpenLink.split(',')

    if (getUA().isWebview) return list.includes('webview')
    if (isDeskTop) return list.includes('pc')
    if (!isDeskTop) return list.includes('mobile')
    return false
  }, [isDeskTop, rootProps?.broadcastOpenLink])

  useAnalysis({ config, partnerId })

  useEffect(() => {
    if (props?.partnerId) setPartnerId(props.partnerId)
    if (props?.memberId) setPartnerId(props.memberId)
    if (props?.memberType) setPartnerId(props.memberType)
    if (props?.renderModuleName) setModuleName(props.renderModuleName)
  }, [props?.partnerId, props?.memberId, props?.memberType, props?.renderModuleName, setModuleName])

  /**
   * 루트Prop 설정 useEffect
   */
  useEffect(() => {
    setRootProps(props)
  }, [props, setRootProps])

  useEffect(() => {
    let copiedConfig = config

    if (copiedConfig.sauceclip && copiedConfig.sauceclip.isSauceClip !== false) {
      copiedConfig = {
        ...copiedConfig,
        sauceclip: {
          ...copiedConfig.sauceclip,
          isSauceClip: true,
        },
      }
    }

    setConfig(copiedConfig)
  }, [config])

  useEffect(() => {
    if (props.bridge || window[BRIDGE.ENTER]) {
      requestBridge(BRIDGE.ENTER, {})
    }
    new GAHelper().init(props.partnerId)
  }, [props])

  useEffect(() => {
    setIsOpenLink((oldState) => getIsOpenLink())
  }, [setIsOpenLink, getIsOpenLink])

  const useTheme = useSetThemeEventListener({ theme, themeB })

  if (!rootProps) {
    return <></>
  }

  let CustomRouter = StaticRouter
  if (router) {
    CustomRouter = BrowserRouter
  }
  if (hashRouter) {
    CustomRouter = HashRouter
  }

  return (
    <ThemeProvider theme={useTheme}>
      <GlobalStyle />
      <div className="sauceflex_collection">
        <CustomRouter {...(router && !hashRouter && { basename: window.location.pathname })}>
          <PageLayout>
            <Switch>
              <Suspense fallback="">
                <Route path="/" exact={true}>
                  <PartnersRouter path="/">
                    <PageMain
                      broadcastDisplayType={props.broadcastDisplayType}
                      broadcastTitle={props.broadcastTitle}
                    />
                  </PartnersRouter>
                </Route>
                <Route
                  path="/curation/:id/:title"
                  exact={true}
                  render={(props) => (
                    <PartnersRouter path="/curation" props={props}>
                      <PageCuration id={props.match.params.id} title={props.match.params.title} />
                    </PartnersRouter>
                  )}
                />
                <Route path="/schedule" exact={true}>
                  <PartnersRouter path="/schedule">
                    <PageSchedule />
                  </PartnersRouter>
                </Route>
                <Route path="/search" exact={true}>
                  <PageSearch />
                </Route>
                <Route
                  path="/search/:keyword"
                  render={(props) => <PageSearchResult keyword={props.match.params.keyword} />}
                  exact={true}
                />
              </Suspense>
            </Switch>
          </PageLayout>
        </CustomRouter>
      </div>
    </ThemeProvider>
  )
}

export default Module
